import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import * as api from "../api";
import { NudgeTemplate, Nudge, NudgePreference } from "../types";

// Query keys
export const nudgeKeys = {
  all: ["nudges"] as const,
  templates: {
    all: ["nudges", "templates"] as const,
    list: () => [...nudgeKeys.templates.all, "list"] as const,
    detail: (id: string) => [...nudgeKeys.templates.all, id] as const,
  },
  nudges: {
    all: ["nudges", "nudges"] as const,
    list: () => [...nudgeKeys.nudges.all, "list"] as const,
    detail: (id: string) => [...nudgeKeys.nudges.all, id] as const,
  },
  preferences: {
    all: ["nudges", "preferences"] as const,
    list: () => [...nudgeKeys.preferences.all, "list"] as const,
  },
};

// Nudge Templates hooks
export function useNudgeTemplatesQuery() {
  return useQuery<NudgeTemplate[]>({
    queryKey: nudgeKeys.templates.list(),
    queryFn: api.getNudgeTemplates,
    staleTime: 1000 * 60 * 3, // 3 minutes
  });
}

export function useNudgeTemplateQuery(id: string) {
  return useQuery<NudgeTemplate>({
    queryKey: nudgeKeys.templates.detail(id),
    queryFn: () => api.getNudgeTemplate(id),
    enabled: !!id,
  });
}

export function useCreateNudgeTemplate() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: api.createNudgeTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nudgeKeys.templates.all });
      toast({
        title: "Nudge template created",
        status: "success",
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating template",
        description: error.response?.data?.errors?.[0] || "Something went wrong",
        status: "error",
        duration: 5000,
      });
    },
  });
}

export function useUpdateNudgeTemplate() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<NudgeTemplate> }) => api.updateNudgeTemplate(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: nudgeKeys.templates.all });
      queryClient.invalidateQueries({ queryKey: nudgeKeys.templates.detail(id) });
      toast({
        title: "Template updated",
        status: "success",
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating template",
        description: error.response?.data?.errors?.[0] || "Something went wrong",
        status: "error",
        duration: 5000,
      });
    },
  });
}

export function useDeleteNudgeTemplate() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: api.deleteNudgeTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nudgeKeys.templates.all });
      toast({
        title: "Template deleted",
        status: "success",
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting template",
        description: error.response?.data?.errors?.[0] || "Something went wrong",
        status: "error",
        duration: 5000,
      });
    },
  });
}

// Nudges hooks
export function useNudgesQuery() {
  return useQuery<Nudge[]>({
    queryKey: nudgeKeys.nudges.list(),
    queryFn: api.getNudges,
    staleTime: 1000 * 60 * 1, // 1 minute - nudges are more real-time
  });
}

export function useDismissNudge() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: api.dismissNudge,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nudgeKeys.nudges.all });
      toast({
        title: "Nudge dismissed",
        status: "success",
        duration: 2000,
      });
    },
  });
}

export function useActUponNudge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.actUponNudge,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nudgeKeys.nudges.all });
    },
  });
}

// Nudge Preferences hooks
export function useNudgePreferencesQuery() {
  return useQuery<NudgePreference[]>({
    queryKey: nudgeKeys.preferences.list(),
    queryFn: api.getNudgePreferences,
    staleTime: 1000 * 60 * 3, // 3 minutes
  });
}

export function useUpdateNudgePreference() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<NudgePreference> }) => api.updateNudgePreference(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nudgeKeys.preferences.all });
      toast({
        title: "Preference updated",
        status: "success",
        duration: 2000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating preference",
        description: error.response?.data?.errors?.[0] || "Something went wrong",
        status: "error",
        duration: 5000,
      });
    },
  });
}
